import { DateTime, Interval } from 'luxon';
import { v4 } from 'uuid';

/** Stable reference to an empty array. Useful to avoid React updates. */
// Using type assertions here to get a result type of any[], which can be assigned to every array type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
const EMPTY_ARRAY = Object.freeze([]);
/**
 * An algorithm to check if an array includes an element of a "related" type, and if it does narrow the
 * searched element to the array type
 *
 * Useful for example to check if a generic IElement is in an array of IElementSections.
 * If the functions returns true the input IElement will be narrowed down to IElementSection after the call
 *
 * @param array of element to search into
 * @param toFind element to find inside the array
 * @param comparator function used to decide if two objects are the same
 * @returns true if toFind is part of array
 */
function includes(array, toFind, comparator = defaultComparator) {
    return array.find((value) => comparator(value, toFind)) !== undefined;
}
/**
 * Compare two items
 *
 * @param a first item
 * @param b second item
 * @returns true if the two items are the same object
 */
function defaultComparator(a, b) {
    return a === b;
}

/**
 * Check if the predicate is valid. If it is false, throw an error.
 *
 * @param predicate The condition to check
 * @param message The message associated to the exception
 */
function assert(predicate, message) {
    if (!predicate) {
        throw new Error(message ?? "Assertion failed");
    }
}

/**
 * Determine if two numbers are approximately equal.
 *
 * Floating point operations can quickly introduce numerical inaccuracies,
 * making this function more robust than direct equality comparisons.
 *
 * @param expected The expected value of the number.
 * @param actual The actual value of the number.
 * @param epsilon The difference between the numbers that's allowed to still consider them to be equal.
 * @returns `true` if the difference between the two numbers is at most `epsilon`, else `false`.
 */
function isCloseTo(expected, actual, epsilon = 1e-4) {
    return Math.abs(actual - expected) <= epsilon;
}

/** The default locale to use for conversion if no locale is passed. In case `defaultLocale` is then `undefined` the system locale is used*/
let defaultLocale;
/**
 * Change the default locale used for time<->string conversions
 *
 * @param locale the locale string to use or undefined to use the system locale
 */
function changeDefaultLocale(locale) {
    defaultLocale = locale;
}
/**
 * @returns The String in the global locale format `{Date} - {Time}`. When `locale` is set then this `locale` for formatting is used
 * Otherwise the `defaultLocale` is used. If `defaultLocale` is not set then the system locale is used.
 * @param timestamp String compatible to parse by Date
 * @param locale The locale String to use for formatting the date and time e.q 'en-US'. If not set first `defaultLocale` is used and
 * if that is not set the system locale is used.
 */
function convertToDateTimeString(timestamp, locale = defaultLocale) {
    const dateObject = new Date(timestamp);
    if (isNaN(dateObject.getTime())) {
        return "Invalid Timestamp";
    }
    const date = dateObject.toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    const time = dateObject
        .toLocaleString(locale, {
        hour: "2-digit",
        hourCycle: "h12",
        minute: "2-digit",
    })
        // Remove whitespace characters and replace them with normal space, this will be removed after fixing the ticket below
        // TODO - https://faro01.atlassian.net/browse/SWEB-1938
        .replace(/\s/g, " ");
    return `${date} - ${time}`;
}
/**
 * @returns Date string in a specific format based on `locale` value. Otherwise the `defaultLocale` is used.
 * If `defaultLocale` is not set then the system locale is used.
 * @param timestamp String compatible to parse by Date
 * @param locale The locale String to use for formatting the date e.q 'en-US'. If not set first `defaultLocale` is used and
 * if that is not set the system locale is used.
 */
function convertToDateString(timestamp, locale = defaultLocale) {
    return new Date(timestamp).toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}
/**
 * @param date object to get the date and time from
 * @returns the date time, in the iso format, without milliseconds and with the timezone offset.
 */
function dateToISOWithOffset(date) {
    // Need to set the ms to 0 otherwise the suppressMilliseconds flag will not work
    date.setMilliseconds(0);
    const isoDate = DateTime.fromJSDate(date).toISO({
        suppressMilliseconds: true,
    });
    assert(isoDate, "The input date should be a valid Date object");
    return isoDate;
}
/**
 * @returns Comparison value of two dates:
 *   - <0 means that dateA is before dateB
 *   - 0 means that dateA and dateB are exactly the same
 *   - >0 means that dateA is after dateB
 * @param dateA First date to use for comparison
 * @param dateB Second date to use for comparison
 */
function compareDateTimes(dateA, dateB) {
    const a = typeof dateA === "string" ? Date.parse(dateA) : dateA.getTime();
    const b = typeof dateB === "string" ? Date.parse(dateB) : dateB.getTime();
    assert(!isNaN(a), "dateA is invalid");
    assert(!isNaN(b), "dateB is invalid");
    return a - b;
}
/**
 * @returns a "merged" date using the date of the first Date and the time form the second Date
 * @param date the Date to use the date from
 * @param time the Date to use the time from
 */
function combineDateAndTime(date, time) {
    assert(!isNaN(date.getTime()), "combineDateAndTime: date is invalid");
    assert(!isNaN(time.getTime()), "combineDateAndTime: time is invalid");
    const dateSource = DateTime.fromJSDate(date);
    const dateTime = DateTime.fromJSDate(time).set({
        day: dateSource.day,
        month: dateSource.month,
        year: dateSource.year,
    });
    return dateTime.toJSDate();
}
/**
 * @returns A string expressing the relative time distance between the given date and now.
 *   Will return "now" if the date is less than one minute ago.
 * @param date Date to convert
 */
function generateRelativeDate(date) {
    const lastMinute = Interval.fromDateTimes(DateTime.now().minus({ seconds: 59 }), DateTime.now()
        // Making sure "now" is shown in case the provided date is now,
        // otherwise "in 0 seconds" is shown.
        .plus({ second: 1 }));
    return lastMinute.contains(date) ? "now" : date.toRelative();
}
/**
 * The minimum and maximum milliseconds from the epoch supported by Date class
 * https://262.ecma-international.org/5.1/#sec-15.9.1.1
 */
const SMALLEST_MILLISECONDS = -8640000000000000;
const LARGEST_MILLISECONDS = 8640000000000000;

/**
 * A set of easing function to modify a 0-1 input to follow a given curve.
 * See {@link https://easings.net/} for a visualization of the various curves.
 */
const Easing = {
    linear(pos) {
        return pos;
    },
    inQuad(pos) {
        return Math.pow(pos, 2);
    },
    outQuad(pos) {
        return -(Math.pow(pos - 1, 2) - 1);
    },
    inOutQuad(pos) {
        if ((pos /= 0.5) < 1)
            return 0.5 * Math.pow(pos, 2);
        return -0.5 * ((pos -= 2) * pos - 2);
    },
    inCubic(pos) {
        return Math.pow(pos, 3);
    },
    outCubic(pos) {
        return Math.pow(pos - 1, 3) + 1;
    },
    inOutCubic(pos) {
        if ((pos /= 0.5) < 1)
            return 0.5 * Math.pow(pos, 3);
        return 0.5 * (Math.pow(pos - 2, 3) + 2);
    },
    inQuart(pos) {
        return Math.pow(pos, 4);
    },
    outQuart(pos) {
        return -(Math.pow(pos - 1, 4) - 1);
    },
    inOutQuart(pos) {
        if ((pos /= 0.5) < 1)
            return 0.5 * Math.pow(pos, 4);
        return -0.5 * ((pos -= 2) * Math.pow(pos, 3) - 2);
    },
    inQuint(pos) {
        return Math.pow(pos, 5);
    },
    outQuint(pos) {
        return Math.pow(pos - 1, 5) + 1;
    },
    inOutQuint(pos) {
        if ((pos /= 0.5) < 1)
            return 0.5 * Math.pow(pos, 5);
        return 0.5 * (Math.pow(pos - 2, 5) + 2);
    },
    inSine(pos) {
        return -Math.cos(pos * (Math.PI / 2)) + 1;
    },
    outSine(pos) {
        return Math.sin(pos * (Math.PI / 2));
    },
    inOutSine(pos) {
        return -0.5 * (Math.cos(Math.PI * pos) - 1);
    },
    inExpo(pos) {
        return pos === 0 ? 0 : Math.pow(2, 10 * (pos - 1));
    },
    outExpo(pos) {
        return pos === 1 ? 1 : -Math.pow(2, -10 * pos) + 1;
    },
    inOutExpo(pos) {
        if (pos === 0)
            return 0;
        if (pos === 1)
            return 1;
        if ((pos /= 0.5) < 1)
            return 0.5 * Math.pow(2, 10 * (pos - 1));
        return 0.5 * (-Math.pow(2, -10 * --pos) + 2);
    },
    inCirc(pos) {
        return -(Math.sqrt(1 - pos * pos) - 1);
    },
    outCirc(pos) {
        return Math.sqrt(1 - Math.pow(pos - 1, 2));
    },
    inOutCirc(pos) {
        if ((pos /= 0.5) < 1)
            return -0.5 * (Math.sqrt(1 - pos * pos) - 1);
        return 0.5 * (Math.sqrt(1 - (pos -= 2) * pos) + 1);
    },
    outBounce(pos) {
        if (pos < 1 / 2.75) {
            return 7.5625 * pos * pos;
        }
        else if (pos < 2 / 2.75) {
            return 7.5625 * (pos -= 1.5 / 2.75) * pos + 0.75;
        }
        else if (pos < 2.5 / 2.75) {
            return 7.5625 * (pos -= 2.25 / 2.75) * pos + 0.9375;
        }
        return 7.5625 * (pos -= 2.625 / 2.75) * pos + 0.984375;
    },
    inBack(pos) {
        const s = 1.70158;
        return pos * pos * ((s + 1) * pos - s);
    },
    outBack(pos) {
        const s = 1.70158;
        return (pos = pos - 1) * pos * ((s + 1) * pos + s) + 1;
    },
    inOutBack(pos) {
        let s = 1.70158;
        if ((pos /= 0.5) < 1) {
            return 0.5 * (pos * pos * (((s *= 1.525) + 1) * pos - s));
        }
        return 0.5 * ((pos -= 2) * pos * (((s *= 1.525) + 1) * pos + s) + 2);
    },
    swingFromTo(pos) {
        let s = 1.70158;
        return (pos /= 0.5) < 1
            ? 0.5 * (pos * pos * (((s *= 1.525) + 1) * pos - s))
            : 0.5 * ((pos -= 2) * pos * (((s *= 1.525) + 1) * pos + s) + 2);
    },
    swingFrom(pos) {
        const s = 1.70158;
        return pos * pos * ((s + 1) * pos - s);
    },
    swingTo(pos) {
        const s = 1.70158;
        return (pos -= 1) * pos * ((s + 1) * pos + s) + 1;
    },
    bouncePast(pos) {
        if (pos < 1 / 2.75) {
            return 7.5625 * pos * pos;
        }
        else if (pos < 2 / 2.75) {
            return 2 - (7.5625 * (pos -= 1.5 / 2.75) * pos + 0.75);
        }
        else if (pos < 2.5 / 2.75) {
            return 2 - (7.5625 * (pos -= 2.25 / 2.75) * pos + 0.9375);
        }
        return 2 - (7.5625 * (pos -= 2.625 / 2.75) * pos + 0.984375);
    },
    fromTo(pos) {
        if ((pos /= 0.5) < 1)
            return 0.5 * Math.pow(pos, 4);
        return -0.5 * ((pos -= 2) * Math.pow(pos, 3) - 2);
    },
    from(pos) {
        return Math.pow(pos, 4);
    },
    to(pos) {
        return Math.pow(pos, 0.25);
    },
    inOutElastic(pos) {
        const c5 = (2 * Math.PI) / 4.5;
        if (pos === 0) {
            return 0;
        }
        else if (pos === 1) {
            return 1;
        }
        return pos < 0.5
            ? -(Math.pow(2, 20 * pos - 10) * Math.sin((20 * pos - 11.125) * c5)) / 2
            : (Math.pow(2, -20 * pos + 10) * Math.sin((20 * pos - 11.125) * c5)) / 2 +
                1;
    },
};
/**
 * Get the 0-1 progress value between a start time and and end time
 * (May go outside 0-1 if tNow > t2 or tNow < t1)
 *
 * @param t1 The start time for the animation
 * @param t2 The end time for the animation
 * @param tNow The current time
 * @returns percent progress between t1 and t2
 */
function getProgress(t1, t2, tNow) {
    return (tNow - t1) / (t2 - t1);
}

/**
 * Error to notify that an async task has been cancelled by an AbortController
 */
class AbortError extends Error {
    /**
     * @param taskAborted The name of the aborted task
     */
    constructor(taskAborted) {
        super(`Task ${taskAborted} aborted`);
    }
}
/**
 * Error to notify that a fetch network connection was successful (the host exists) but the requested resource does not exists
 */
class FetchError extends Error {
    /**
     * @param host The host we queried
     * @param missingResource The missing resource
     */
    constructor(host, missingResource) {
        super(`The resource ${missingResource} was not found on ${host}`);
    }
}
/**
 * Error to notify that a server responded, but had a HTTP status outside of the 200-299 range.
 */
class FetchResponseError extends Error {
    response;
    /**
     * @param response the response of the the request
     */
    constructor(response) {
        super(`HTTP ${response.status} for request ${response.url}`);
        this.response = response;
    }
}
/**
 * Error to signal that a parsing of some xml/json failed
 */
class ParsingError extends Error {
    /**
     * @param what A description of the file or resources that we failed to parse
     */
    constructor(what) {
        super(`Parsing of ${what} failed`);
    }
}
/**
 * Error to signal that some configuration had an invalid or incompatible value
 */
class InvalidConfigurationError extends Error {
    /**
     * @param what a description of the invalid configuration
     */
    constructor(what) {
        super(what);
    }
}
/**
 * Error to signal that we're accessing an array out of the valid range
 */
class RangeError extends Error {
    /**
     * @param what The error description
     */
    constructor(what) {
        super(what);
    }
}
/**
 * Check if an error received by LotV code or by the browser is a download abort
 *
 * @param error The error or string received
 * @returns true if that was an abort
 */
function isDownloadAbortError(error) {
    if (error instanceof AbortError)
        return true;
    if (typeof error === "string") {
        // Check error message for abort on chrome
        if (error === "The user aborted a request.")
            return true;
        // Check error message for abort on firefox
        if (error.includes("The operation was aborted."))
            return true;
        // Check error message for abort  on iPad Pro
        if (error.includes("Fetch is aborted"))
            return true;
    }
    return false;
}
/**
 * @param error to check
 * @returns true if it's a network connection error from the fetch api
 */
function isNetworkConnectionError(error) {
    if (!(error instanceof Error)) {
        return false;
    }
    // Check error message for fetch api network errors on chrome
    if (error.message === "Failed to fetch") {
        return true;
    }
    // Check error message for fetch api network errors on firefox
    if (error.message === "NetworkError when attempting to fetch resource.") {
        return true;
    }
    // Check error message for fetch api network errors on safari
    if (error.message === "Load failed") {
        return true;
    }
    return false;
}

/**
 * Extracts the file name from a given URL, with an option to include or exclude the file extension.
 *
 * @param url the URL string from which to extract the file name. It should be a valid URL-encoded string.
 * @param shouldHaveExtension a boolean indicating whether the returned file name should include the file extension. Defaults to `true`.
 * @returns The extracted file name as a string, or `undefined` if the URL does not contain a valid file name.
 */
function getFileNameFromUrl(url, shouldHaveExtension = true) {
    // Decode the URL to handle special characters like spaces (%20)
    const decodedUrl = decodeURIComponent(url);
    // Remove the query string and fragment
    const urlWithoutQuery = decodedUrl.split("?")[0];
    const urlWithoutFragment = urlWithoutQuery.split("#")[0];
    // Split the URL by slashes and get the last part which should be the file name
    const urlParts = urlWithoutFragment.split("/");
    const lastPart = urlParts[urlParts.length - 1] || "";
    if (!lastPart) {
        return;
    }
    if (shouldHaveExtension) {
        return lastPart.trim();
    }
    const lastDotIndex = lastPart.lastIndexOf(".");
    return lastDotIndex === -1
        ? lastPart.trim()
        : lastPart.substring(0, lastDotIndex).trim();
}
/**
 * Get the extension of the provided URI
 *
 * @param uri The URI to grab the extension from
 * @returns Either the extension, or `undefined` in case the URI does not have an extension
 */
function getFileExtension(uri) {
    return uri?.includes(".") ? uri.split(".").pop() : undefined;
}
/**
 * @returns a filename without the extension
 * @param fileName to remove the extension from
 */
function removeExtension(fileName) {
    if (fileName === undefined)
        return;
    const lastDot = fileName.lastIndexOf(".");
    if (lastDot !== -1) {
        return fileName.substring(0, lastDot);
    }
    return fileName;
}
/**
 * @returns the file name and extension
 * @param fileName name of the file that needs to be split into extension and name
 */
function splitFileNameAndExtension(fileName) {
    const splitResult = fileName.split(".");
    const extension = splitResult.length > 1 ? splitResult.pop() : undefined;
    return { fileName: splitResult.join("."), extension };
}
const IMAGE_FILE_EXTENSIONS = ["png", "jpg", "jpeg"];
/**
 * @returns whether the file extensions is a known image file extension.
 * @param extension the extension of the file to check
 */
function isImageFileExtension(extension = "") {
    return IMAGE_FILE_EXTENSIONS.includes(extension.toLowerCase());
}
/**
 * @returns whether the file extensions is a known document file extension.
 * @param extension the extension of the file to check
 */
function isDocumentFileExtension(extension = "") {
    return extension.toLowerCase() === "pdf";
}

// Bitmask to mask individual byte
const BITMASK_BYTE = 0xff;
// The string length of the UUID portion in the GUID
const UUID_PORTION = 19;
/**
 * Generates a GUID that is accepted by the ProjectAPI.
 *
 * @param date Date to encode into the UUID
 * @returns Sequential version of an UUID v4, contains the timestamps encoded
 */
function generateGUID(date = new Date()) {
    const uuid = v4();
    // Convert current date time to UTC ticks
    const ticks = unixTimestampToDotNetTicks(date.getTime());
    const tickBytes = longToBytes(ticks);
    // See https://github.com/dotnet/efcore/blob/main/src/EFCore/ValueGeneration/SequentialGuidValueGenerator.cs
    const encodedTimestamp = [
        tickBytes[1].toString(16).padStart(2, "0"),
        tickBytes[0].toString(16).padStart(2, "0"),
        "-",
        tickBytes[7].toString(16).padStart(2, "0"),
        tickBytes[6].toString(16).padStart(2, "0"),
        tickBytes[5].toString(16).padStart(2, "0"),
        tickBytes[4].toString(16).padStart(2, "0"),
        tickBytes[3].toString(16).padStart(2, "0"),
        tickBytes[2].toString(16).padStart(2, "0"),
    ].join("");
    return uuid.substring(0, UUID_PORTION) + encodedTimestamp;
}
/**
 * Taken from https://stackoverflow.com/a/12965194
 *
 * @param {number} long Number to convert
 * @returns {number[]} Resulting byte array
 */
function longToBytes(long) {
    const byteArray = [0, 0, 0, 0, 0, 0, 0, 0];
    for (let index = 0; index < byteArray.length; index++) {
        const byte = long & BITMASK_BYTE;
        byteArray[index] = byte;
        long = (long - byte) / 256;
    }
    return byteArray;
}
/**
 * Converts a UNIX epoch timestamp to .net ticks
 *
 * This is necessary as the JavaScript Date type's origin is the Unix epoch (midnight on 1 January 1970),
 * while the .NET DateTime type's origin is midnight on 1 January 0001
 *
 * @param {number} unixTimestamp Timestamp to convert
 * @returns {number} .net time
 */
function unixTimestampToDotNetTicks(unixTimestamp) {
    // the number of .net ticks at the unix epoch
    const epochTicks = 621355968000000000;
    // there are 10000 .net ticks per millisecond
    const ticksPerMillisecond = 10000;
    return unixTimestamp * ticksPerMillisecond + epochTicks;
}

/**
 * @returns A human readable size of bytes with unit (e.g 212MB)
 * @param bytes the number of bytes to show
 * @param digits the number of digits to show
 */
function humanReadableBytes(bytes, digits = 0) {
    if (bytes === 0)
        return "0 Bytes";
    const k = 1024;
    const units = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(digits))} ${units[i]}`;
}

/** A unique symbol indicating that the object is being loaded, e.g. fetched from an API. */
const LOADING = Symbol("Loading");
/**
 * @param value The value to check
 * @returns True if the value is currently loading, else false.
 */
function isLoading(value) {
    return value === LOADING;
}
/**
 * @param value The value to check.
 * @returns True if the value is fully loaded and defined, else false.
 */
function isDefinedAndLoaded(value) {
    return value !== undefined && !isLoading(value);
}

let loggingEnabled = false;
let guardsThrowing = true;
/**
 * Enable or disable the diagnostic logging for the IElement type guards
 * It can be useful to investigate why a type guard fails but imposes a too big overhead
 * in production
 *
 * @param enabled true to enable logging of failure for IElement type guards
 */
function toggleGuardsLogging(enabled) {
    loggingEnabled = enabled;
}
/** @returns True if IElement type guards logging is enabled */
function isGuardsLoggingEnabled() {
    return loggingEnabled;
}
/**
 * @param enabled True to throw ApiValidationError on invalid API response.
 * False to log the error instead.
 */
function toggleGuardsThrowing(enabled) {
    guardsThrowing = enabled;
}
/**
 * @returns True to throw ApiValidationError on invalid API response.
 * False to log the error instead.
 */
function areGuardsThrowing() {
    return guardsThrowing;
}
/**
 * Execute a function with logging temporarily enabled or disabled.
 *
 * @param enabled True/false to temporariy enable/disable logging.
 * @param fn Synchronous function to execute with the temp logging setting.
 * @returns `fn()`
 */
function withGuardsLogging(enabled, fn) {
    const wasLogging = loggingEnabled;
    loggingEnabled = enabled;
    try {
        return fn();
    }
    finally {
        loggingEnabled = wasLogging;
    }
}
/**
 * Log a type failure, if logging is enabled
 *
 * @param message message to log
 * @param args additional arguments to log
 */
function log(message, ...args) {
    // TODO: Re enable when we implement a proper logging system https://faro01.atlassian.net/browse/SWEB-459
    // eslint-disable-next-line no-console
    if (loggingEnabled)
        console.error(message, ...args);
}

/**
 * Create a logistic function with the given parameters.
 *
 * Creates an S-shaped curve where the results are in the range [0, `capacity`].
 *
 * Useful to map unbounded numbers into a fixed range.
 *
 * @see https://en.wikipedia.org/wiki/Logistic_function
 * @returns The logistic function
 */
function logisticFunction({ capacity = 1, growthRate = 1, midPoint = 0, } = {}) {
    return (value) => capacity / (1 + Math.pow(Math.E, -growthRate * (value - midPoint)));
}

/**
 * @returns True if the platform is MacOS, false otherwise.
 */
function isMacOS() {
    return navigator.userAgent.toLowerCase().includes("mac");
}

/**
 * Execute an async function and retry if failed
 *
 * @param f The async function to execute
 * @param options to configure how to handle the retry
 * @returns the value returned by f when it succeeded or the last failure
 */
async function retry(f, options = {}) {
    const { max = 10, shouldRetry = () => true, delay = 1000 } = options;
    assert(max > 0, "Invalid number of max retries, need to be at least 1");
    assert(typeof delay !== "number" || delay >= 0, "Invalid delay, need to be at least 0");
    // This loop capture all tries until the last
    let effectiveDelay = undefined;
    for (let i = 0; i < max - 1; ++i) {
        try {
            return await f();
        }
        catch (reason) {
            if (!shouldRetry(reason)) {
                throw reason;
            }
        }
        effectiveDelay = Math.max(typeof delay === "number" ? delay : delay(i + 1, effectiveDelay), 0);
        // Wait for the delay before the next try
        if (effectiveDelay > 0) {
            await new Promise((resolve) => globalThis.setTimeout(resolve, effectiveDelay));
        }
    }
    // If we reach here this is the last chance so it's ok to just invoke the function
    return f();
}
/** Max waiting time on exponential back-off */
const MAX_BACK_OFF_MS = 64000;
/**
 * A function compatible with the retry delay parameters to implement exponential back-off.
 * It returns (2^(iteration-1) + random in [-0.5, 0.5)) seconds, as milliseconds.
 * -> Expected average values: [1, 2, 4, 8,  ..., 64, 64, ...] seconds.
 * -> Value ranges:            Average value +/- 0.5 seconds.
 *
 * Reference: https://cloud.google.com/iot/docs/how-tos/exponential-backoff
 *
 * @param iteration retry iteration (1 = 1st retry).
 * @param lastDelay the delay used for the last iteration if available [milliseconds]
 * @returns how long to wait for the next try [milliseconds]
 */
function exponentialBackOff(iteration, lastDelay) {
    if (lastDelay === MAX_BACK_OFF_MS) {
        return lastDelay;
    }
    // retry() calls us with iteration = 1 for the first retry, so we decrease the iteration by 1
    // and subtract 0.5 seconds to get an expected average value of 1 second for the first delay.
    iteration = Math.max(0, iteration - 1);
    return Math.min((Math.pow(2, iteration) + Math.random() - 0.5) * 1000, MAX_BACK_OFF_MS);
}

/** HTTP status for too many requests */
const HTTP_TOO_MANY_REQUESTS = 429;
/** HTTP status indicating an internal server error */
const HTTP_INTERNAL_SERVER_ERROR = 500;
/**
 * A fetch wrapper that adds retry logic in case of network errors.
 * Should only be used with idempotent requests.
 *
 * @param input passed to the fetch() method
 * @param init passed to the fetch() method
 * @returns the response of the request
 */
function robustFetch(input, init) {
    return retry(async () => {
        const res = await fetch(input, init);
        if (!res.ok) {
            throw new FetchResponseError(res);
        }
        return res;
    }, {
        max: 3,
        delay: exponentialBackOff,
        shouldRetry: (error) => 
        // retry on unknown network errors (e.g. no internet, or closed connection)
        isNetworkConnectionError(error) ||
            (error instanceof FetchResponseError &&
                // retry, when the request was rate limited
                (error.response.status === HTTP_TOO_MANY_REQUESTS ||
                    // retry on server errors
                    error.response.status >= HTTP_INTERNAL_SERVER_ERROR)),
    });
}
/**
 * A fetch wrapper that adds retry logic in case of network errors.
 * Should only be used with idempotent requests.
 *
 * @returns the validated json object of a successful response
 */
async function robustFetchJson({ input, init, validateJson, }) {
    const res = await robustFetch(input, init);
    const json = await res.json();
    assert(validateJson(json), `Unexpected response for request: ${res.url}`);
    return json;
}

/**
 * Class to emit events with a specific type and have type checked listeners
 */
class TypedEvent {
    listeners = [];
    listenersOnce = [];
    /**
     * Attach a listener for this event
     *
     * @param listener The listener function
     * @returns An object that can be used to disconnect this connection
     */
    on = (listener) => {
        this.listeners.push(listener);
        return {
            dispose: () => this.off(listener),
        };
    };
    /**
     * Attach a listener that will receive the event only once
     *
     * @param listener The listener
     */
    once = (listener) => {
        this.listenersOnce.push(listener);
    };
    /**
     * Detach a listener
     *
     * @param listener the listener to detach
     */
    off = (listener) => {
        const callbackOnIndex = this.listeners.indexOf(listener);
        if (callbackOnIndex > -1)
            this.listeners.splice(callbackOnIndex, 1);
        const callbackOnceIndex = this.listenersOnce.indexOf(listener);
        if (callbackOnceIndex > -1)
            this.listenersOnce.splice(callbackOnceIndex, 1);
    };
    /**
     * Emit an event, will call all listeners
     *
     * @param event The event to forward to all listeners
     */
    emit = (event) => {
        /** Update any general listeners */
        for (const listener of this.listeners)
            listener(event);
        /** Clear the `once` queue */
        if (this.listenersOnce.length > 0) {
            const toCall = this.listenersOnce;
            this.listenersOnce = [];
            for (const listener of toCall)
                listener(event);
        }
    };
    /**
     * Pipe all events to another emitter
     *
     * @param te The emitter to pipe all events to
     * @returns An object to detach the receiving emitter
     */
    pipe = (te) => this.on((e) => te.emit(e));
    /**
     * Wait for the next time the event is emitted
     *
     * @returns a promise that will resolve once the event is emitted
     */
    next = () => new Promise((success) => this.once(success));
}

/** Offset to expire the urls a bit sooner, to account for delays between the url being issued and the request being made */
const EXPIRATION_OFFSET_MS = 60 * 1000;
/** A sentinel date used for a never expiring url */
const EXPIRATION_NEVER_DATE = new Date("2999-01-01T00:00:00.000Z");
/**
 * A wrapper for a signed url that can be updated if expired
 */
class SignedUrl {
    url;
    refreshUrl;
    data;
    urlUpdated = new TypedEvent();
    /**
     * Construct a new SignedUrl
     *
     * @param url the base url to sign
     * @param refreshUrl a function to compute an updated signed url
     * @param data the initial signature if available
     */
    constructor(url, refreshUrl, data) {
        this.url = url;
        this.refreshUrl = refreshUrl;
        this.data = data;
    }
    /** @returns true if the signature is not too close to the expiration time */
    get isValid() {
        return this.timeUntilExpiredMs > EXPIRATION_OFFSET_MS;
    }
    /** @returns how many milliseconds until this signature will expire */
    get timeUntilExpiredMs() {
        if (!this.data)
            return 0;
        return new Date(this.data.expiresOn).getTime() - Date.now();
    }
    /** @returns The original, unsigned URL of the resource */
    get unsignedUrl() {
        return this.url;
    }
    /** @returns the signed url if valid or undefined otherwise */
    get signedUrl() {
        if (this.isValid && this.data) {
            return this.data.signedUrl;
        }
    }
    /** @returns an updated signed url, requesting a new one if needed */
    async requestSignedUrl() {
        if (!this.data || !this.isValid) {
            return (await this.updateUrl()).signedUrl;
        }
        return this.data.signedUrl;
    }
    /**
     * Request a new signed url and stores it in the cache
     *
     * @returns the new signed url data
     */
    async updateUrl() {
        this.data = await this.refreshUrl(this.url);
        this.urlUpdated.emit(this.data);
        return this.data;
    }
}
/**
 * A special signed url that never expires
 */
class UnsignedUrl extends SignedUrl {
    /**
     * Construct a SignedUrl instance that never expires
     *
     * @param url to treat as forever signed
     */
    constructor(url) {
        super(url, () => Promise.resolve({
            signedUrl: url,
            expiresOn: EXPIRATION_NEVER_DATE,
        }), {
            signedUrl: url,
            expiresOn: EXPIRATION_NEVER_DATE,
        });
    }
}

/**
 * @returns the index of the nth occurrence of the pattern in text
 * @param text to search into
 * @param pattern to search
 * @param nth occurrence index to return (starting from 1 for the 1st occurrence)
 */
function nthIndexOf(text, pattern, nth) {
    if (!pattern)
        return;
    const R = new RegExp(pattern, "g");
    const matches = [...text.matchAll(R)];
    return matches.at(nth - 1)?.index;
}
/**
 * Parse a string that is expected to be an url inferring the protocol if missing
 *
 * @param link string that may miss the protocol
 * @returns a complete url string with the protocol at the start
 */
function parseUrlLikeString(link) {
    assert(link.length > 0, "Invalid empty url string");
    // Return the original string if it started with a valid protocol
    const VALID_PROTOCOLS = ["http:", "https:", "mailto:", "ftp:", "file:"];
    if (VALID_PROTOCOLS.some((protocol) => link.startsWith(protocol))) {
        return link;
    }
    // Check if the link resemble an email
    if (mayBeEmail(link)) {
        return `mailto:${link}`;
    }
    // Fallback to a https for everything else
    return `https://${link}`;
}
/**
 * @param text to check
 * @returns true if the text may be an email
 *
 * NOTE: There's no safe way to validate an email client side
 * this function uses a regex that may reject valid email address
 * so it can't be used for real validation
 */
function mayBeEmail(text) {
    const R = /[\w\d!#$%&'*+/=?^_`{|}~-]+(?:\.[\w\d!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w\d](?:[\w\d]*[\w\d])?\.)+[\w\d](?:[\w\d-]*[\w\d])?/;
    return R.test(text);
}

/** Allow a limited number of parallel async tasks */
class TaskPool {
    /** Running tasks */
    #tasks = new Map();
    /** Max number of parallel task allowed */
    #size;
    /** Next task id to use  */
    #nextId = 0;
    /** @returns the max number of parallel task allowed in this pool */
    get size() {
        return this.#size;
    }
    /** @returns the number of running tasks in the queue */
    get runningTasks() {
        return this.#tasks.size;
    }
    /** @returns true if this pool is full and can't accept new tasks */
    get full() {
        return this.runningTasks >= this.size;
    }
    /**
     * Manage a limited queue of async tasks
     *
     * @param size max number of async task that this pool can run
     */
    constructor(size) {
        if (size <= 0) {
            throw new Error("TaskPool size should be greater than 0");
        }
        this.#size = size;
    }
    /**
     * Run a new task in the pool if there's enough capacity
     *
     * @param task to run
     * @returns the id of the new task or undefined if the pool was full
     */
    tryRun(task) {
        if (this.full) {
            return;
        }
        const id = this.#generateId();
        const taskPromise = task();
        const poolPromise = this.#runOne(id, taskPromise);
        this.#tasks.set(id, { taskPromise, poolPromise });
        return id;
    }
    /**
     * Wait for the next task to finish
     *
     * @returns the result of the task
     */
    async waitOne() {
        // Extract all the pool promises to check the next that will finish
        const runningTasks = Array.from(this.#tasks.values()).map(({ poolPromise }) => poolPromise);
        // Wait for the next task to finish and get the id
        const next = await Promise.race(runningTasks);
        return this.waitFor(next);
    }
    /**
     * Wait for all the remaining tasks in the pool
     *
     * @returns the result of all the tasks
     * @throws {WaitAllError} an error if at least one of the running tasks failed
     */
    async waitAll() {
        const results = [];
        const errors = [];
        for (const taskId of this.#tasks.keys()) {
            try {
                results.push(await this.waitFor(taskId));
            }
            catch (error) {
                errors.push(error);
            }
        }
        if (errors.length > 0) {
            throw new WaitAllError(errors);
        }
        return results;
    }
    /**
     * Wait for a specific task to finish
     *
     * @param taskId id of the task in the pool to await
     * @returns the task result or error
     * @throws an error if there is no running task in the queue with that id
     */
    waitFor(taskId) {
        // Get the finished task and remove it from the queue
        const task = this.#tasks.get(taskId);
        if (!task) {
            return Promise.reject(new Error("Finished task is not part of the pool"));
        }
        this.#tasks.delete(taskId);
        // Return the awaited task result
        return task.taskPromise;
    }
    /**
     * Wait until there's a free spot in the queue.
     *
     * @returns the result of the awaited task
     */
    waitForFreeSpot() {
        if (this.full) {
            return this.waitOne();
        }
        return Promise.resolve(undefined);
    }
    /**
     * Run a task inside a Pool managed wrapper task that will return the task id on completion
     *
     * @param id unique id of this task inside this pool
     * @param task to run
     * @returns the id of the task as soon as it finishes
     */
    async #runOne(id, task) {
        try {
            await task;
        }
        catch {
            /* the internal promise result/error will be unwrapped in the waitFor method */
        }
        return id;
    }
    /**
     * @returns a new unique id for a task in this queue
     */
    #generateId() {
        const id = this.#nextId;
        this.#nextId++;
        return id;
    }
}
/**
 * An error raised when awaiting for multiple tasks when one of the tasks fail
 */
class WaitAllError extends Error {
    taskErrors;
    /**
     * Error to notify one or more tasks inside the pool failed while waiting for all of them
     *
     * @param taskErrors the errors thrown by the failed tasks inside the TaskPool
     */
    constructor(taskErrors) {
        super("One or more tasks in the TaskPool failed");
        this.taskErrors = taskErrors;
    }
}

/** Symbol used to notify a property can not be null or undefined in the IElement type definition */
const PropRequired = Symbol("PropRequired");
/** Symbol used to notify that a property can be null or undefined in the IElement type definition */
const PropOptional = Symbol("PropOptional");
/**
 * @returns true if the prop is missing but optional, false if it's not missing or not optional
 * @param prop to check
 * @param optionality to validate if the prop is optional
 */
function isOptionalPropMissing(prop, optionality) {
    if (prop !== null && prop !== undefined) {
        return false;
    }
    return optionality === PropOptional;
}
/**
 * Verify a property on an object is of a required primitive type
 *
 * @param object to check
 * @param prop to check
 * @param type required
 * @param optionality to define if this property can be null
 * @returns true if the prop is of the required type
 */
function validatePrimitive(object, prop, type, optionality = PropRequired) {
    // @ts-expect-error the validity of the property is checked in the body of the function
    const toCheck = object[prop];
    return (isOptionalPropMissing(toCheck, optionality) ||
        check(typeof toCheck === type, "Property", prop, "with value", toCheck, "is not of type", type));
}
/**
 * Verify that a property is a non empty string
 *
 * @param object to check
 * @param prop to check
 * @param optionality to define if this property can be null
 * @returns true if the prop is a non empty string
 */
function validateNonEmptyString(object, prop, optionality = PropRequired) {
    return (isOptionalPropMissing(object[prop], optionality) ||
        (validatePrimitive(object, prop, "string") &&
            check(object[prop] !== "", `Property ${String(prop)} should not be empty`)));
}
/**
 * Check a value is an object and is not null or undefined
 *
 * An expected type `T` can be specified in the generic argument to pre-type the object as `Partial<T>` after validation.
 * Note that the types of the properties *still need to be validated*, this functionality only exists
 * to remove some typing boilerplate in the validation logic.
 *
 * @param object to check
 * @param name of the object we expect to receive for logging purposes as it can't be checked in Javascript
 * @returns true if it's not null or undefined and is an object and not another primitive type
 */
function validateNotNullishObject(object, name) {
    if (!object) {
        log(`expected object is ${object} instead of ${name}`);
        return false;
    }
    if (typeof object !== "object") {
        log(`expected object is of type ${typeof object} instead of ${name}`);
        return false;
    }
    return true;
}
/**
 * Verify a property has a specific value
 *
 * @param object to check
 * @param prop to check
 * @param expectedValue to check
 * @returns true if the prop has the expected value
 */
function validateOfExpectedValue(object, prop, expectedValue) {
    if (object[prop] !== expectedValue) {
        log(`Property ${String(prop)} is not matching the expected value ${expectedValue}`);
        return false;
    }
    return true;
}
/**
 * @returns true if the prop is an array containing object of a specific type
 */
function validateArrayOf({ object, prop, elementGuard, size, optionality = PropRequired, }) {
    // @ts-expect-error the validity of the property is checked in the body of the function
    const toCheck = object[prop];
    return (isOptionalPropMissing(toCheck, optionality) ||
        check(Array.isArray(toCheck) &&
            toCheck.every(elementGuard) &&
            (!size || toCheck.length === size), "Property", prop, "with value", toCheck, "is not an array matching", elementGuard, "and size", size));
}
/**
 * Verify that a property is a valid value in an array
 *
 * @param object to check
 * @param prop to check
 * @param e enum to check for
 * @param optionality to define if this property can be null
 * @returns true if the prop is a valid enum
 */
function validatePropertyEnumValue(object, prop, e, optionality = PropRequired) {
    const value = object[prop];
    return (isOptionalPropMissing(value, optionality) ||
        check(validateEnumValue(value, e), "Property", prop, "with value", value, "is not valid for enum", Object.values(e)));
}
/**
 * Check if the value is an instance of the enum.
 *
 * @param value The value to check.
 * @param e The enum that the value might be part of.
 * @returns True, if the value is an instance of the enum.
 */
function validateEnumValue(value, e) {
    return Object.values(e).includes(value);
}
/**
 * Verify that a property on an object is of a required type
 *
 * @param object to check
 * @param prop to check
 * @param typeGuard to check if the object respect the expected type
 * @param optionality to define if this property can be null
 * @returns true if the prop is of the required type
 */
function validateOfType(object, prop, typeGuard, optionality = PropRequired) {
    const value = object[prop];
    return (isOptionalPropMissing(value, optionality) ||
        check(typeGuard(value), "Property", prop, "with value", value, "does not respect the type guard", typeGuard));
}
/**
 * Log a message if the passed condition is not met and return the condition for further logic
 *
 * @param condition condition checked
 * @param message message to log to notify what failed
 * @param args additional arguments to log
 * @returns the same condition for further logic
 */
function check(condition, message, ...args) {
    if (!condition) {
        log(message, ...args);
    }
    return condition;
}

/**
 * @param hostname If not provided, globalThis.location.hostname is used.
 * @returns Whether the provided hostname is one of the various possibilities for localhost.
 */
function isLocalhost(hostname = globalThis.location.hostname) {
    return (hostname === "localhost" ||
        // [::1] is the IPv6 localhost address.
        hostname === "[::1]" ||
        hostname === "::1" ||
        // 127.0.0.0/8 are considered localhost for IPv4.
        !!hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/));
}

/**
 * Walk a generic data structure with a queue to not use recursion
 *
 * @param queue The initial nodes of the data-structure to visit
 * @param compute A function that will take the value to compute and a callback to append more nodes to visit
 * @returns the value returned by the compute callback, or undefined if compute never returns anything
 */
function walkWithQueue(queue, compute) {
    function append(...toQueue) {
        queue.push(...toQueue);
    }
    let val = queue.shift();
    while (val !== undefined) {
        const ret = compute(val, append);
        if (ret) {
            return ret;
        }
        val = queue.shift();
    }
}

export { AbortError, EMPTY_ARRAY, EXPIRATION_NEVER_DATE, Easing, FetchError, FetchResponseError, InvalidConfigurationError, LARGEST_MILLISECONDS, LOADING, MAX_BACK_OFF_MS, ParsingError, PropOptional, PropRequired, RangeError, SMALLEST_MILLISECONDS, SignedUrl, TaskPool, TypedEvent, UnsignedUrl, WaitAllError, areGuardsThrowing, assert, changeDefaultLocale, combineDateAndTime, compareDateTimes, convertToDateString, convertToDateTimeString, dateToISOWithOffset, defaultLocale, exponentialBackOff, generateGUID, generateRelativeDate, getFileExtension, getFileNameFromUrl, getProgress, humanReadableBytes, includes, isCloseTo, isDefinedAndLoaded, isDocumentFileExtension, isDownloadAbortError, isGuardsLoggingEnabled, isImageFileExtension, isLoading, isLocalhost, isMacOS, isNetworkConnectionError, isOptionalPropMissing, log, logisticFunction, mayBeEmail, nthIndexOf, parseUrlLikeString, removeExtension, retry, robustFetch, robustFetchJson, splitFileNameAndExtension, toggleGuardsLogging, toggleGuardsThrowing, validateArrayOf, validateEnumValue, validateNonEmptyString, validateNotNullishObject, validateOfExpectedValue, validateOfType, validatePrimitive, validatePropertyEnumValue, walkWithQueue, withGuardsLogging };
